import React, { useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  SortHeader,
  PaginationContext,
  SortContext,
  SearchContext,
  Text,
  ITableFilter,
  Badge,
} from "dash-ui";
import { AccountStatusesEnum } from "dash-common";

import {
  OrganizationUser,
  useGetOrganizationUsersByOrganizationRoleQuery,
} from "gql/generated.graphql";
import { usePermissionCheck } from "common/hooks/usePermissionCheck/usePermissionCheck";
import DeliveryImage from "assets/images/png/Delivery.png";
import SearchingImage from "assets/images/png/Searching.png";

import { USER_STATUSES_BADGE_TYPE } from "./OrganizationUsersTable.constants";
import OrganizationUserTableRowActions from "./components/OrgazniationUserTableRowActions";
import { TableWitPaginationWraper } from "./OrganizationUsersTable.style";

function OrganizationUsersTable({ filter }: { filter: ITableFilter }) {
  const { t } = useTranslation("tables", {
    keyPrefix: "organizationUsers",
  });
  const { t: tStatuses } = useTranslation("common");
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });
  const { t: tPlaceholders } = useTranslation("common", {
    keyPrefix: "placeholders",
  });
  const { t: tForms } = useTranslation("forms", { keyPrefix: "labels" });
  const {
    setTotal,
    state: paginationState,
    resetPagination,
    getRefetchProps,
  } = useContext(PaginationContext);
  const { searchSlug } = useContext(SearchContext);
  const { state: sort } = useContext(SortContext);
  const { skip, limit } = getRefetchProps(paginationState);
  const searchText = searchSlug.length > 2 ? searchSlug : null;
  const isPermitted = usePermissionCheck();

  const {
    data: result,
    refetch,
    loading,
  } = useGetOrganizationUsersByOrganizationRoleQuery({
    variables: {
      sort: [sort],
      pagination: { skip, limit },
      filter: {
        searchText,
        organizationId: filter.organizationId,
      },
    },
  });
  const { data, total } = useMemo(
    () => ({
      data: result?.getOrganizationUsersByOrganizationRole?.collection || [],
      total: result?.getOrganizationUsersByOrganizationRole?.totalCount || 0,
    }),
    [result]
  );

  // FIXME: re-check memo
  const columns = React.useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="profile" isDefault sortOrder={-1}>
            {tLabels`name`}
          </SortHeader>
        ),
        accessor: "profile",
        Cell: ({
          value,
        }: {
          value: { firstName: string; lastName: string };
        }) => <Text>{value.lastName ? `${value.firstName} ${value.lastName}` : value.firstName}</Text>,
      },
      {
        Header: () => (
          <SortHeader accessor="account.email">{tForms`email`}</SortHeader>
        ),
        accessor: "account.email",
      },
      {
        Header: () => (
          <SortHeader accessor="account.role.type">{tForms`role`}</SortHeader>
        ),
        accessor: "roleId",
        Cell: ({
          row: { original },
        }: {
          row: { original: OrganizationUser };
        }) => {
          return (
            <Text>
              {tStatuses(
                `common:roles.${
                  original?.saleTeamRole?.type || original?.account?.role?.type
                }`
              )}
            </Text>
          );
        },
      },
      {
        Header: () => (
          <SortHeader accessor="account.status">{tLabels`status`}</SortHeader>
        ),
        accessor: "account.status",
        Cell: ({ value }: { value: AccountStatusesEnum }) => (
          <Badge
            title={tStatuses(`common:accountStatuses.${value}`)}
            type={USER_STATUSES_BADGE_TYPE[value]}
          />
        ),
      },
      ...(isPermitted
        ? [
            {
              Header: () => <Text>{tLabels`actions`}</Text>,
              accessor: "_id",
              Cell: ({
                row: { original },
              }: {
                row: { original: OrganizationUser };
              }) => <OrganizationUserTableRowActions user={original} />,
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tLabels, isPermitted]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSlug]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    refetch({
      sort: [sort],
      pagination: { skip, limit },
      filter: {
        searchText,
        organizationId: filter.organizationId,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, sort, searchSlug]);

  const labelToShow = !loading
    ? (searchText ? tPlaceholders`noSearchResults` : t`placeholder`) || ""
    : "";

  const imageToShow = !loading
    ? searchText
      ? SearchingImage
      : DeliveryImage
    : undefined;

  return (
    <TableWitPaginationWraper
      tableProps={{
        data,
        columns,
        loading,
        placeholderProps: {
          image: imageToShow,
          label: labelToShow,
        },
      }}
      paginationProps={{
        label: t`paginationLabel`,
        previousButtonLabel: tLabels`previous`,
        nextButtonLabel: tLabels`next`,
      }}
    />
  );
}

export default OrganizationUsersTable;
